import { useEffect, useRef } from "react";

import { useDispatchDrawers } from "contexts/drawers";
import { useDispatchSidePanels } from "contexts/sidePanels";
import { CORE_WINDOW_IDS, DEBUG_UTILS_WINDOW_ID, OCTREE_DEBUG_WINDOW_ID, useDispatchWindows } from "contexts/windows";
import { isNovolibDebug } from "utils/novolibDebug";

import { DebugUtilsWindow } from "./core/debugUtils/debugUtilsWindow";
import { DimensionWindow } from "./core/dimension/dimensionWindow";
import { ObjectTreeWindowRouter } from "./core/objectTree/objectTreeWindowRouter";
import { OctreeDebugWindow } from "./core/octreeDebug/octreeDebugWindow";
import { PointCloudSettingsWindow } from "./core/pointCloudSettings/pointCloudSettingsWindow";
import { PropertiesWindow } from "./core/properties/propertiesWindow";
import { SettingsWindow } from "./core/settings/settingsWindow";
import { registerInternalWindow } from "./registerInternalWindow";

/**
 * Registers all core internal windows (Settings, Properties, ObjectTree, Dimension)
 * across the floating window, side panel, and drawer stores.
 *
 * Call once at the app level — the same SlottedWindow-based component is used in
 * all three contexts; `useWindowPlatformMode` handles adaptation internally.
 */
export function useCoreWindowRegistration() {
    const dispatchWindows = useDispatchWindows();
    const dispatchPanels = useDispatchSidePanels();
    const dispatchDrawers = useDispatchDrawers();
    const hasRegistered = useRef(false);

    useEffect(() => {
        if (hasRegistered.current) {
            return;
        }

        const dispatchers = {
            windows: dispatchWindows,
            panels: dispatchPanels,
            drawers: dispatchDrawers,
        };

        registerInternalWindow(
            {
                id: CORE_WINDOW_IDS.SETTINGS,
                title: "Settings",
                component: SettingsWindow,
                defaultPosition: {
                    elementId: "quick-access-camera",
                    placement: "left",
                    align: { vertical: "top" },
                },
                dockable: false,
            },
            dispatchers,
        );

        registerInternalWindow(
            {
                id: CORE_WINDOW_IDS.PROPERTIES,
                title: "Properties",
                component: PropertiesWindow,
            },
            dispatchers,
        );

        registerInternalWindow(
            {
                id: CORE_WINDOW_IDS.OBJECT_TREE,
                title: "Objects",
                component: ObjectTreeWindowRouter,
            },
            dispatchers,
        );

        registerInternalWindow(
            {
                id: CORE_WINDOW_IDS.DIMENSION,
                title: "Dimension",
                component: DimensionWindow,
            },
            dispatchers,
        );

        registerInternalWindow(
            {
                id: CORE_WINDOW_IDS.RENDER_POINT_CLOUD,
                title: "Point Cloud Settings",
                component: PointCloudSettingsWindow,
                dockable: false,
            },
            dispatchers,
        );

        if (isNovolibDebug()) {
            registerInternalWindow(
                { id: DEBUG_UTILS_WINDOW_ID, title: "Debug utils", component: DebugUtilsWindow },
                dispatchers,
            );
            registerInternalWindow(
                { id: OCTREE_DEBUG_WINDOW_ID, title: "Octree Debug", component: OctreeDebugWindow, dockable: true },
                dispatchers,
            );
        }

        hasRegistered.current = true;
    }, [dispatchWindows, dispatchPanels, dispatchDrawers]);
}
